/* KMF unified filter client
 * Owns: Sort + Active pills + In Stock + Price + Gender + Type
 *
 * DATA SOURCE (Plan: 20260630-124303 — collection-storefront-pagination):
 *  - Collection pages + /search empty-browse stream from the Storefront API (window.KM2Brands
 *    .createProductPager), 50 products per page on scroll, FULLY SERVER-SIDE filtered + sorted.
 *    Nothing is filtered client-side on these paths — the sidebar maps straight to the query.
 *  - Typed /search (?q=) keeps its client-side full-load of /collections/all/products.json,
 *    because description (body_html) search legitimately needs the whole set and the Storefront
 *    product query cannot match body_html.
 *
 * URL contract:
 *   ?sort=manual|price-asc|price-desc|title-asc|title-desc|created-desc   (default: title-asc, A→Z)
 *   &stock=0            (sold-out shown when 0; in-stock-only is the default)
 *   &pmin=0&pmax=100    (price units, integer dollars)
 *   &g=women,men        (gender tags csv)
 *   &t=type-eau-de-parfum,...  (fragrance-type tags csv — mapped to productType server-side)
 *   &v=<Vendor>         (vendor deep-link from homepage tiles)
 *   &q=<term>&qf=name,brand,description,tags  (typed /search only)
 *
 * Combine semantics (server-side): cross-facet AND, same-facet OR. Fragrance-type maps to
 * `productType` (NOT a tag) so it ANDs with the gender tag instead of OR-ing with it.
 *
 * Edge cases handled:
 *  - Multiple roots in DOM (desktop + mobile drawer): all roots stay in sync.
 *  - Initial URL deep link: pre-checks inputs, builds the pager from URL state on first render.
 *  - Storefront error: restores the server-rendered Liquid grid (never blank).
 *  - History back/forward: popstate re-applies state.
 *  - Race conditions: applyToken (load) + feedGen (scroll-advance) — latest call wins.
 */
(function () {
  if (window.__kmfInit) return;
  window.__kmfInit = true;

  const GRID = '#product-grid';
  const CONTAINER = '#ProductGridContainer';
  const PAGE_SIZE = 250;         // typed-/search products.json page size
  const MAX_PAGES = 8;           // typed-/search full-load cap (2000-product safety bound)
  const WINDOW = 50;             // products streamed per page / per advance step
  const SCROLL_NEAR = 20;        // advance when within N cards of the end
  const DEFAULT_SORT = 'title-asc'; // A→Z by product name (plan requirement)

  // Fragrance-type tag → Shopify productType label. Type MUST reach the server as productType
  // (not a {tag:type-*}) so it ANDs with the gender tag (probe-proven: tag+tag ORs wrongly).
  const TYPE_PRODUCTTYPE = {
    'type-eau-de-toilette': 'Eau de Toilette',
    'type-eau-de-parfum': 'Eau de Parfum',
    'type-eau-de-cologne': 'Eau de Cologne',
    'type-eau-fraiche': 'Eau Fraiche',
    'type-body-spray': 'Body Spray',
  };

  function onSearch() { return location.pathname.replace(/\/+$/, '') === '/search'; }
  // /collections/all is Shopify's VIRTUAL "all products" collection: collection(handle:"all") is
  // NULL in the Storefront API, so collection.products(handle:"all") errors and the grid would fall
  // back to the server-rendered Liquid cards. Treat handle "all" (and any empty handle) as a virtual
  // collection so it streams through the top-level products(query) pager instead — same path /search
  // empty-browse uses (frosted badge + working in-stock filter come free).
  function isVirtualCollection(handle) {
    const h = String(handle || '').toLowerCase();
    return h === '' || h === 'all';
  }

  // --- URL state ---
  function readState() {
    const sp = new URLSearchParams(location.search);
    return {
      sort: sp.get('sort') || DEFAULT_SORT,
      // In-stock-only is ON by default — sold-out products are hidden unless the
      // shopper explicitly turns it off (?stock=0 / unchecking the box).
      stock: sp.get('stock') !== '0',
      pmin: sp.has('pmin') ? Number(sp.get('pmin')) : null,
      pmax: sp.has('pmax') ? Number(sp.get('pmax')) : null,
      g: (sp.get('g') || '').split(',').filter(Boolean),
      t: (sp.get('t') || '').split(',').filter(Boolean),
      // Vendor deep-link (?v=<Vendor>) — set by homepage audience tiles. Not a sidebar control:
      // writeState leaves it untouched so it survives any sidebar filter interaction.
      v: sp.get('v') || '',
      // Free-text search query (?q=) — typed /search only.
      q: sp.get('q') || '',
      // Active match fields for ?q= (?qf=name,brand,description,tags). Default name+brand.
      qf: (sp.get('qf') || 'name,brand').split(',').map(s => s.trim()).filter(Boolean),
    };
  }
  function writeState(s, root) {
    const sp = new URLSearchParams(location.search);
    sp.delete('sort'); if (s.sort && s.sort !== DEFAULT_SORT) sp.set('sort', s.sort);
    sp.delete('stock'); if (!s.stock) sp.set('stock', '0');
    sp.delete('pmin'); sp.delete('pmax');
    const max = Number(root.dataset.kmfPriceCeiling || 0);
    if (s.pmin !== null && s.pmin > 0) sp.set('pmin', String(s.pmin));
    if (s.pmax !== null && s.pmax < max) sp.set('pmax', String(s.pmax));
    sp.delete('g'); if (s.g.length) sp.set('g', s.g.join(','));
    sp.delete('t'); if (s.t.length) sp.set('t', s.t.join(','));
    sp.delete('qf');
    const qfDefault = s.qf.length === 2 && s.qf.includes('name') && s.qf.includes('brand');
    if (s.qf.length && !qfDefault) sp.set('qf', s.qf.join(','));
    const qs = sp.toString();
    history.pushState({}, '', location.pathname + (qs ? '?' + qs : ''));
  }

  // --- Cache (typed-/search full-load only) ---
  const cache = { products: null, loading: null, savedGrid: null, savedPagination: null, full: false, failed: false, pages: [], failedAt: 0 };
  // A failed catalogue page is retried at most this often, and only that page: retrying the whole
  // catalogue on every keystroke multiplied the load that made it fail (code review 2026-10-12).
  const CATALOGUE_RETRY_MS = 30000;
  // The query the server-rendered grid (cache.savedGrid) was rendered for. The live search box
  // rewrites ?q= in place, so after typing, that grid belongs to a DIFFERENT query.
  const pageLoadQuery = new URLSearchParams(location.search).get('q') || '';

  // --- Authoritative availability (Storefront API) — typed /search ONLY ---
  // products.json reflects the ONLINE-STORE channel, where this store's products can read
  // available:false even while in stock on the headless Storefront the site sells on. The streamed
  // grid (collection + /search browse) reads availability from the Storefront node directly
  // (availableForSale), so it needs no overlay. Typed /search still loads products.json (for
  // body_html description matching), so it keeps the Storefront in-stock overlay below.
  let availHandles = null, availLoading = null;
  function loadAvailability() {
    if (availHandles) return Promise.resolve(availHandles);
    if (availLoading) return availLoading;
    const K = window.KM2Brands;
    if (!K || !K.createProductPager || !K.config || !K.config.token) return Promise.resolve(null); // graceful
    availLoading = (async () => {
      try {
        // Walk the FULL available set. A page {error} yields a PARTIAL set that would wrongly hide
        // in-stock items (and let the wrong products.json availability drive the badge) — so on any
        // page error, rebuild the spent pager and retry; only a COMPLETE walk is trusted, else null.
        let set = new Set(), complete = false;
        for (let attempt = 0; attempt <= MAX_AVAIL_RETRIES && !complete; attempt++) {
          if (attempt > 0) await sleep(RETRY_BACKOFF_MS * attempt);
          const pager = K.createProductPager({ query: 'available_for_sale:true', sortKey: 'BEST_SELLING' });
          set = new Set();
          let errored = false;
          for (let i = 0; i < 60; i++) {         // safety-capped page walk
            const pg = await pager.nextPage();
            if (pg && pg.error) { errored = true; break; }   // transient failure → retry a fresh pager
            ((pg && pg.nodes) || []).forEach(n => { if (n && n.handle) set.add(String(n.handle).toLowerCase()); });
            if (!pg || !pg.hasMore) { complete = true; break; }
          }
          if (errored) complete = false;
        }
        availHandles = complete ? set : null;    // partial/failed → null: fall back to variant.available
      } catch (e) {
        console.warn('[kmf-filters] Storefront availability fetch failed; using products.json availability', e);
        availHandles = null;                     // fall back to variant.available
      }
      availLoading = null;
      return availHandles;
    })();
    return availLoading;
  }

  // Typed-/search full-catalogue walk: up to MAX_PAGES of PAGE_SIZE over /collections/all.
  async function fetchPage(source, p) {
    try {
      const url = `/collections/${encodeURIComponent(source)}/products.json?limit=${PAGE_SIZE}&page=${p}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      return ((await res.json()) || {}).products || [];
    } catch (e) {
      console.warn('[kmf-filters] fetch failed page', p, e);
      return null;
    }
  }
  // True while the catalogue needs no fetch: fully loaded, or failed recently (cool-down).
  function catalogueSettled() {
    return cache.full || (cache.failed && Date.now() - cache.failedAt < CATALOGUE_RETRY_MS);
  }
  async function loadFullCatalogue() {
    if (catalogueSettled()) return cache.products;
    if (cache.loading) return cache.loading;
    cache.loading = (async () => {
      // /search typed-search loads the whole catalogue once — fetch all pages IN PARALLEL so the
      // first-search warm-up is ~1 round-trip, not MAX_PAGES sequential ones. A retry after a
      // failure re-asks only the pages that failed; pages already read are kept.
      const reqs = [];
      for (let p = 1; p <= MAX_PAGES; p++) reqs.push(cache.pages[p - 1] != null ? cache.pages[p - 1] : fetchPage('all', p));
      const results = await Promise.all(reqs);
      cache.pages = results;
      // A page that failed to load (null) is NOT "no products on that page": count it as a failure
      // so a search never reports "no products match" for a catalogue it could not read, and do
      // not cache the partial set as the full catalogue (audit 2026-10-05 #3).
      cache.failed = results.some(r => r === null);
      cache.failedAt = cache.failed ? Date.now() : 0;
      cache.products = [].concat(...results.map(r => r || []));
      cache.full = !cache.failed;
      cache.loading = null;
      return cache.products;
    })();
    return cache.loading;
  }

  // --- Storefront node → card model adapter -----------------------------------------------------
  // Delegates to the ONE shared contract (plan 20260901-050844 T5). This function used to be one
  // of six divergent mappers; the mapping now lives in assets/km2-product-model.js so the grid,
  // the rails and the tiles cannot drift apart again.
  //
  // Two things the shared adapter now does that this copy did NOT, both of which matter here:
  //   - it carries a variant `title`, which is what a size picker renders (the point of the plan);
  //   - it keeps product-level availableForSale SEPARATE from variants[0].available, instead of
  //     folding the product-level flag down onto the first variant.
  //
  // That second change is why the old comment here ("availableForSale ... becomes
  // variants[0].available, so productAvailable then needs no overlay") is retired: it was never
  // quite true — productAvailable keeps its availHandles overlay (AC25), because this store's
  // products.json availability is wrong for the headless channel. Ownership of that overlay stays
  // in this file; the model carries the field and decides no policy.
  function toCardModel(node) {
    if (!node) return { handle: '', title: '', vendor: '', tags: [], images: [], variants: [] };
    var M = window.KM2ProductModel;
    if (!M) {
      // Fail loudly rather than silently rendering blank cards: the module is included globally
      // from layout/theme.liquid, so its absence is a load-order bug, not a data condition.
      if (window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'kmf-filters');
      return { handle: node.handle || '', title: node.title || '', vendor: '', tags: [], images: [], variants: [] };
    }
    return M.productFromStorefrontNode(node);
  }

  // --- Sidebar facets → Storefront query (A03: structured variables, never string-concatenated) --
  // Collection path → ProductFilter[] for collection.products(filters:). Cross-facet AND, same-facet
  // OR (Storefront's native combine semantics: same filter-type ORs, different types AND).
  function buildFilters(state) {
    const f = [];
    if (state.stock) f.push({ available: true });
    const price = {};
    if (state.pmin !== null && state.pmin > 0) price.min = state.pmin;
    if (state.pmax !== null) price.max = state.pmax;
    if (price.min !== undefined || price.max !== undefined) f.push({ price: price });
    state.g.forEach(g => f.push({ tag: g }));                                  // OR within gender
    state.t.forEach(t => { const pt = TYPE_PRODUCTTYPE[t]; if (pt) f.push({ productType: pt }); }); // ANDs with gender
    if (state.v) f.push({ productVendor: state.v });
    return f;
  }
  // /search browse path → Shopify storefront search query string for top-level products(query:).
  // Same AND-across / OR-within semantics expressed in search syntax. '' means "all products"
  // (used when the shopper turns the in-stock filter off with no other facet active).
  function sq(v) { return '"' + String(v).replace(/"/g, '\\"') + '"'; }
  function buildSearchQuery(state) {
    const parts = [];
    if (state.stock) parts.push('available_for_sale:true');
    if (state.v) parts.push('vendor:' + sq(state.v));
    if (state.g.length) parts.push('(' + state.g.map(g => 'tag:' + sq(g)).join(' OR ') + ')');
    if (state.t.length) {
      const types = state.t.map(t => TYPE_PRODUCTTYPE[t]).filter(Boolean).map(pt => 'product_type:' + sq(pt));
      if (types.length) parts.push('(' + types.join(' OR ') + ')');
    }
    if (state.pmin !== null && state.pmin > 0) parts.push('variants.price:>=' + state.pmin);
    if (state.pmax !== null) parts.push('variants.price:<=' + state.pmax);
    return parts.join(' AND ');
  }
  // sort → sortKey + reverse. Collection uses ProductCollectionSortKeys (CREATED), top-level
  // products() uses ProductSortKeys (CREATED_AT) — the key names differ, so map per path.
  function collectionSortKey(sort) {
    switch (sort) {
      case 'price-asc':   return { key: 'PRICE', reverse: false };
      case 'price-desc':  return { key: 'PRICE', reverse: true };
      case 'title-desc':  return { key: 'TITLE', reverse: true };
      case 'created-desc':return { key: 'CREATED', reverse: true };
      case 'manual':      return { key: 'COLLECTION_DEFAULT', reverse: false }; // "Most Relevant"
      case 'title-asc':
      default:            return { key: 'TITLE', reverse: false };              // A→Z default
    }
  }
  function topLevelSortKey(sort) {
    switch (sort) {
      case 'price-asc':   return { key: 'PRICE', reverse: false };
      case 'price-desc':  return { key: 'PRICE', reverse: true };
      case 'title-desc':  return { key: 'TITLE', reverse: true };
      case 'created-desc':return { key: 'CREATED_AT', reverse: true };
      case 'manual':      return { key: 'RELEVANCE', reverse: false };
      case 'title-asc':
      default:            return { key: 'TITLE', reverse: false };
    }
  }

  // Build the Storefront pager for the current state. Returns null when the client is absent.
  function buildPager(state, handle) {
    const K = window.KM2Brands;
    if (!K || !K.createProductPager || !K.config || !K.config.token) return null;
    if (onSearch() || isVirtualCollection(handle)) {
      const sk = topLevelSortKey(state.sort);
      return K.createProductPager({
        query: buildSearchQuery(state), sortKey: sk.key, reverse: sk.reverse, pageSize: WINDOW,
      });
    }
    const sk = collectionSortKey(state.sort);
    return K.createProductPager({
      collectionHandle: handle, filters: buildFilters(state),
      sortKey: sk.key, reverse: sk.reverse, pageSize: WINDOW,
    });
  }

  // --- Card render (matches server-rendered card-product structure) ---
  function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  function fmtMoney(symbol, n) {
    return `${symbol}${Number(n || 0).toFixed(2)} USD`;
  }
  // The price a grid card prints — KM2ProductModel.displayPrice (finding 513): cheapest IN-STOCK
  // size inside the shopper's price facet, "From" when dearer sizes exist, compare-at from that
  // same size. The same call orders the page (orderByShownPrice), so the number sorted on IS the
  // number printed. variants[0] is only the fallback for a load-order bug (model absent).
  function shownPrice(p, range) {
    const M = window.KM2ProductModel;
    if (!M) {
      if (window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'kmf-filters');
      const v0 = (p.variants || [])[0];
      return { price: v0 ? parseFloat(v0.price) : 0, compare_at_price: v0 && v0.compare_at_price ? parseFloat(v0.compare_at_price) : 0, from: false };
    }
    return M.displayPrice(p, range);
  }
  function priceRange(state) { return { min: state && state.pmin !== null ? state.pmin : null, max: state && state.pmax !== null ? state.pmax : null }; }

  function cardHtml(p, currencySymbol, range) {
    const imgs = (p.images || []).map(i => i.src);
    // 700px renditions via the ONE resize helper (D14). Model absence is a load-order bug that
    // shownPrice() below already reports through KM2Deps; the raw src keeps the card's picture.
    const M = window.KM2ProductModel;
    const sized = (u) => (M ? M.imageUrl(u, 700) : u);
    const first = imgs[0] ? sized(imgs[0]) : '';
    const second = imgs[1] ? sized(imgs[1]) : '';
    const avail = productAvailable(p); // authoritative (Storefront when loaded) — no false 'Sold out'
    const dp = shownPrice(p, range);
    const price = dp.price || 0;
    const compare = dp.compare_at_price || 0;
    const onSale = compare > price;
    const ratio = '125.0%';
    const priceText = fmtMoney(currencySymbol, price);
    const shown = dp.from && window.KM2ProductCard ? window.KM2ProductCard.fromPrice(priceText) : priceText;
    const priceInner = onSale
      ? `<div class="price__sale"><span class="price-item price-item--sale">${escapeHtml(shown)}</span> <s class="price-item price-item--regular">${escapeHtml(fmtMoney(currencySymbol, compare))}</s></div>`
      : `<div class="price__regular"><span class="price-item price-item--regular">${escapeHtml(shown)}</span></div>`;
    const priceCls = `price${onSale ? ' price--on-sale' : ''}${avail ? '' : ' price--sold-out'}`;
    // Type badge (option-2 card, 2026-07-03): reuse the ONE typeAbbr map exported by
    // km2-product-card.js so kmf's client grid matches the server + rails exactly.
    // product_type is now the ONE key: T5 moved toCardModel onto the shared contract, which
    // normalises Storefront `productType` and REST `type` onto `product_type` (round-4 r4-f5).
    // The `p.productType` read is kept as a defensive fallback for any raw node that reaches
    // here unmapped — notably the typed-/search products.json path, which feeds cardHtml with
    // NO mapper at all (the declared seventh source).
    const ptype = p.productType || p.product_type || '';
    const badge = (window.KM2ProductCard && window.KM2ProductCard.typeAbbr) ? window.KM2ProductCard.typeAbbr(ptype) : '';
    return `<li class="grid__item">
      <div class="product_card underline-links-hover">
        <div class="card card--card card--media" style="--ratio-percent: ${ratio};">
          <div class="card-wrapper card__inner ratio" style="--ratio-percent: ${ratio};">
            <div class="card__media kmf-card-loading">
              <div class="media media--transparent media--hover-effect">
                <a href="/products/${encodeURIComponent(p.handle)}" class="media media--transparent media--hover-effect full-unstyled-link">
                  ${first ? `<img class="motion-reduce fisrt-Image" src="${escapeHtml(first)}" alt="${escapeHtml(p.title)}" loading="lazy" />` : ''}
                  ${second ? `<img class="motion-reduce fisrt-Image" src="${escapeHtml(second)}" alt="${escapeHtml(p.title)}" loading="lazy" />` : ''}
                </a>
                ${avail && window.KM2ProductCard ? window.KM2ProductCard.quickAddButtonHTML({ handle: p.handle, variants: p.variants }) : ''}
              </div>
            </div>
            ${avail ? '' : '<div class="card__content"><div class="card__badge top left price--sold-out"><span class="badge price__badge-sold-out">Sold out</span></div></div>'}
          </div>
          <div class="card__content">
            <div class="card__information">
              ${p.vendor ? `<div class="caption-with-letter-spacing light">${escapeHtml(p.vendor)}</div>` : ''}
              <span class="card__heading">
                <a href="/products/${encodeURIComponent(p.handle)}" class="full-unstyled-link h5">${escapeHtml(p.title)}</a>
              </span>
              <div class="km2-price-row">
                <div class="${priceCls}"><div class="price__container">${priceInner}</div></div>
                ${badge ? `<span class="km2-frag-type">${escapeHtml(badge)}</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>`;
  }

  // --- Filter + sort (typed /search ONLY — server paths filter/sort server-side) ---
  function tagSet(arr) { return new Set((arr || []).map(t => String(t).toLowerCase())); }
  function productMinPrice(p) {
    const variants = p.variants || [];
    let min = Infinity;
    for (const v of variants) {
      const pr = parseFloat(v.price);
      if (!isNaN(pr) && pr < min) min = pr;
    }
    return min === Infinity ? 0 : min;
  }
  function productAvailable(p) {
    // Storefront overlay is authoritative when loaded (typed /search); otherwise the per-variant
    // available flag — which, for streamed grid cards, IS the Storefront availableForSale truth
    // (mapped by toCardModel). products.json's Online-Store flag is only seen on the typed path,
    // where availHandles overrides it.
    if (availHandles) return availHandles.has(String(p.handle || '').toLowerCase());
    // AC31: prefer the PRODUCT-LEVEL field ahead of the variant-scan FALLBACK. The product-level
    // flag means "any variant sells", while the scan only sees the FETCHED window — so without
    // this, the sold-out badge / in-stock filter / sold-out-last sort depend on how many variants
    // the query happened to return. Absent field => the variant scan is unchanged (no delta).
    if (p.availableForSale != null) return !!p.availableForSale;
    return (p.variants || []).some(v => v.available);
  }
  function applyFilter(products, state) {
    const q = (state.q || '').trim().toLowerCase();
    return products.filter(p => {
      if (state.stock && !productAvailable(p)) return false;
      if (state.v && String(p.vendor || '').toLowerCase() !== state.v.toLowerCase()) return false;
      if (q) {
        const f = state.qf || ['name', 'brand'];
        const parts = [];
        if (f.includes('name')) parts.push(String(p.title || ''));
        if (f.includes('brand')) parts.push(String(p.vendor || ''));
        if (f.includes('description')) parts.push(String(p.body_html || '').replace(/<[^>]*>/g, ' '));
        if (f.includes('tags')) parts.push((p.tags || []).join(' '));
        if (parts.join(' ').toLowerCase().indexOf(q) === -1) return false;
      }
      const tags = tagSet(p.tags);
      if (state.g.length && !state.g.some(t => tags.has(t))) return false;
      if (state.t.length && !state.t.some(t => tags.has(t))) return false;
      const price = productMinPrice(p);
      if (state.pmin !== null && price < state.pmin) return false;
      if (state.pmax !== null && price > state.pmax) return false;
      return true;
    });
  }
  function applySort(products, state) {
    const sort = state.sort;
    const range = priceRange(state);
    // Price sorts order by the number the card PRINTS (finding 513) — never a different key.
    const shown = p => { const n = shownPrice(p, range).price; return n === null || n === undefined ? Infinity : n; };
    const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0); // explicit: Infinity - Infinity is NaN
    const arr = products.slice();
    switch (sort) {
      case 'price-asc':  arr.sort((a, b) => cmp(shown(a), shown(b))); break;
      case 'price-desc': arr.sort((a, b) => cmp(shown(b), shown(a))); break;
      case 'title-asc':  arr.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'title-desc': arr.sort((a, b) => b.title.localeCompare(a.title)); break;
      case 'created-desc': arr.sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0)); break;
    }
    // Availability is the PRIMARY key under every sort (stable sort preserves
    // the chosen order within the in-stock group and within the sold-out group).
    arr.sort((a, b) => (productAvailable(b) ? 1 : 0) - (productAvailable(a) ? 1 : 0));
    return arr;
  }

  // --- UI sync ---
  const PRETTY = { 'women': 'Women', 'men': 'Men', 'unisex': 'Unisex', 'boys': 'Boys', 'girls': 'Girls', 'type-eau-de-toilette': 'Eau de Toilette', 'type-eau-de-parfum': 'Eau de Parfum', 'type-eau-de-cologne': 'Eau de Cologne', 'type-eau-fraiche': 'Eau Fraiche', 'type-body-spray': 'Body Spray' };
  function pretty(t) { return PRETTY[t] || t; }

  function setLoading(on) {
    const c = document.querySelector(CONTAINER);
    if (!c) return;
    c.classList.toggle('kmf-loading', on);
    const inner = c.querySelector('.collection');
    if (inner) inner.classList.toggle('loading', on);
  }

  // Centered Ripple loader overlay — shown during the first fetch / any filter-change refetch and
  // cleared once the first page renders. Honors prefers-reduced-motion via CSS (static dimmed pad).
  // One cohesive loader: the Ripple on top, an optional caption BELOW it (never overlapping). The
  // caption (e.g. "Searching the full catalogue…" on typed /search) lives INSIDE this overlay rather
  // than as a separate grid message, so the two can't collide.
  function showCenteredLoader(on, caption) {
    const c = document.querySelector(CONTAINER);
    if (!c) return;
    let el = c.querySelector('[data-kmf-loader]');
    if (on) {
      if (!el) {
        el = document.createElement('div');
        el.className = 'kmf-loader';
        el.setAttribute('data-kmf-loader', '');
        el.setAttribute('role', 'status');
        el.setAttribute('aria-label', caption || 'Loading products');
        el.innerHTML = '<div class="kmf-ripple" aria-hidden="true"><span></span><span></span></div>';
        c.appendChild(el);
      }
      // Caption sits below the ripple (added/updated via textContent — XSS-safe).
      let p = el.querySelector('.kmf-loader__text');
      if (caption) {
        if (!p) { p = document.createElement('p'); p.className = 'kmf-loader__text'; el.appendChild(p); }
        p.textContent = caption;
      } else if (p) {
        p.remove();
      }
    } else if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }
  // Per-card image ripple: each new card carries `.kmf-card-loading` (a CSS ripple in the image
  // area). Clear it once that card's image actually loads (or errors) — real load events, no
  // inline handlers. Already-cached images that are `complete` are cleared immediately.
  function armCardLoaders(scopeEl) {
    const cards = (scopeEl || document).querySelectorAll('.card__media.kmf-card-loading');
    cards.forEach(media => {
      const img = media.querySelector('img');
      if (!img) { media.classList.remove('kmf-card-loading'); return; }
      const clear = () => media.classList.remove('kmf-card-loading');
      if (img.complete && img.naturalWidth > 0) { clear(); return; }
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
    });
  }

  function syncRoot(root, state, products, computeCounts) {
    const symbol = root.dataset.kmfCurrencySymbol || '$';
    const max = Number(root.dataset.kmfPriceCeiling || 0);

    // Sort dropdown
    const sortEl = root.querySelector('[data-kmf-sort]');
    if (sortEl && sortEl.value !== state.sort) sortEl.value = state.sort;

    // Stock toggle
    const stockEl = root.querySelector('[data-kmf-stock]');
    if (stockEl) stockEl.checked = state.stock;

    // Price slider + display
    const minEl = root.querySelector('input[data-kmf-price-min]');
    const maxEl = root.querySelector('input[data-kmf-price-max]');
    const minDisp = root.querySelector('[data-kmf-price-min-display]');
    const maxDisp = root.querySelector('[data-kmf-price-max-display]');
    const fillEl = root.querySelector('[data-kmf-price-fill]');
    const curMin = state.pmin !== null ? state.pmin : 0;
    const curMax = state.pmax !== null ? state.pmax : max;
    if (minEl) minEl.value = curMin;
    if (maxEl) maxEl.value = curMax;
    if (minDisp) minDisp.textContent = `${symbol}${curMin}`;
    if (maxDisp) maxDisp.textContent = `${symbol}${curMax}`;
    if (fillEl && max > 0) {
      fillEl.style.left = (curMin / max * 100) + '%';
      fillEl.style.right = ((max - curMax) / max * 100) + '%';
    }

    // Tag checkboxes + counts. computeCounts is true only on the typed-/search path, where the full
    // catalogue is in the client. On the server-streamed paths the full set isn't local, so per-facet
    // counts/disabled can't be computed (plan Decision #5 fallback) — keep every facet enabled,
    // count-less; an empty filter result simply streams the empty state.
    root.querySelectorAll('[data-kmf-tag]').forEach(input => {
      const tag = input.dataset.kmfTag;
      const cat = input.dataset.kmfCat; // 'g' or 't'
      input.checked = state[cat].includes(tag);
      const countEl = input.closest('.sidebar__item')?.querySelector('[data-kmf-count]');
      if (!computeCounts) {
        input.disabled = false;
        input.closest('label, .sidebar__item')?.querySelector('label')?.classList.remove('sidebar-checkbox--disabled');
        if (countEl) countEl.textContent = '';
        return;
      }
      const presentInCollection = products.some(p => tagSet(p.tags).has(tag));
      input.disabled = !presentInCollection && !input.checked;
      input.closest('label, .sidebar__item')?.querySelector('label')?.classList.toggle('sidebar-checkbox--disabled', input.disabled);
      const probeState = { ...state, [cat]: [tag] };
      const n = applyFilter(products, probeState).length;
      if (countEl) countEl.textContent = ` (${n})`;
    });

    // Active pills
    const activeWrap = root.querySelector('[data-kmf-active]');
    const pillsWrap = root.querySelector('[data-kmf-pills]');
    if (activeWrap && pillsWrap) {
      const pills = [];
      if (state.stock) pills.push({ kind: 'stock', label: 'In stock only' });
      if (state.pmin !== null && state.pmin > 0) pills.push({ kind: 'pmin', label: `From ${symbol}${state.pmin}` });
      if (state.pmax !== null && state.pmax < max) pills.push({ kind: 'pmax', label: `Up to ${symbol}${state.pmax}` });
      state.g.forEach(g => pills.push({ kind: 'g', tag: g, label: pretty(g) }));
      state.t.forEach(t => pills.push({ kind: 't', tag: t, label: pretty(t) }));
      activeWrap.hidden = pills.length === 0;
      pillsWrap.innerHTML = pills.map(p =>
        `<a href="#" class="kmf-pill" data-kmf-pill-kind="${p.kind}" ${p.tag ? `data-kmf-pill-tag="${p.tag}"` : ''}>
          ${escapeHtml(p.label)}<span aria-hidden="true">×</span>
        </a>`).join('');
    }
  }

  // Sync the sidebar product-count label(s) to the rendered card count.
  function updateCount(shown, total) {
    const root = document.querySelector('[data-kmf-root]');
    if (!root) return;
    const simpleTpl = root.dataset.kmfCountSimple || '@@N@@ products';
    const ofTpl = root.dataset.kmfCountOf || '@@N@@ of @@T@@ products';
    const text = shown >= total
      ? simpleTpl.replace('@@N@@', shown)
      : ofTpl.replace('@@N@@', shown).replace('@@T@@', total);
    document.querySelectorAll('#ProductCountDesktop, #ProductCount').forEach(el => { el.textContent = text; });
  }

  // kmf owns the grid, so the theme's Ajaxinate infinite-scroll must NOT also append raw server
  // pages on scroll. Emptying the next-page <a> uses Ajaxinate's own "no link -> stop" path.
  function neutralizePagination() {
    const pag = document.getElementById('AjaxinatePagination');
    if (!pag) return;
    if (cache.savedPagination === null) cache.savedPagination = pag.innerHTML;
    pag.innerHTML = '';
    pag.style.display = 'none';
  }
  function restorePagination() {
    const pag = document.getElementById('AjaxinatePagination');
    if (!pag) return;
    if (cache.savedPagination !== null) pag.innerHTML = cache.savedPagination;
    pag.style.display = '';
  }

  // --- Windowed feed (collection + /search) -----------------------------------------------------
  // renderedCount = cards currently in the grid. feedPager/feedExhausted drive the server-streamed
  // paths (collection + /search browse); cache.products drives the typed-/search client path.
  let renderedCount = 0;
  let feedPager = null, feedExhausted = false;
  // Server-stream cards held back from a page because their PRINTED price belongs later (finding
  // 513) — merged into the next page, flushed when the feed ends. Reset by renderReset().
  let feedHeld = [];

  // Re-order one streamed page by the price the cards print (finding 513).
  //
  // Shopify's PRICE sort ranks a product on its cheapest size EVEN WHEN SOLD OUT; we print the
  // cheapest IN-STOCK size. Ours is a min over a subset of the same sizes, so it is never below
  // Shopify's key — under Low→High a card can only need to move LATER, never earlier. So: any card
  // whose printed price exceeds the page's last Shopify key (productMinPrice of the last node) is
  // held and merged into the next page, until it fits or the feed ends. That makes the ascending
  // stream exactly monotone across page boundaries. Under High→Low a card can only need to move
  // EARLIER, which a later page cannot do: the page is ordered in place and the residual is bounded
  // by the gap between a product's cheapest and cheapest-in-stock size (measured 2026-09-11:
  // 3 of 296 in-stock products, all of them on page 1).
  function orderByShownPrice(items, state, hasMore) {
    const sort = state.sort;
    if (sort !== 'price-asc' && sort !== 'price-desc') { feedHeld = []; return items; }
    const range = priceRange(state);
    // Unpriced cards sort last (Infinity); compare explicitly — `Infinity - Infinity` is NaN.
    const shown = p => { const n = shownPrice(p, range).price; return n === null || n === undefined ? Infinity : n; };
    const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
    let pool = feedHeld.concat(items);
    feedHeld = [];
    if (sort === 'price-asc' && hasMore && items.length) {
      // Shopify's key for the LAST product on this page = the page's max key (its order is
      // monotone). Take the max over the page rather than the last card: a last card with no
      // priced size reads 0 and would otherwise hold back the entire page.
      const boundary = Math.max(0, ...items.map(productMinPrice));
      feedHeld = pool.filter(p => shown(p) > boundary);
      pool = pool.filter(p => shown(p) <= boundary);
    }
    pool.sort((a, b) => sort === 'price-asc' ? cmp(shown(a), shown(b)) : cmp(shown(b), shown(a))); // stable
    return pool;
  }

  function renderReset() {
    const g = document.querySelector(GRID);
    if (g) {
      // Capture the pristine server-rendered Liquid grid before the first clear, so a Storefront
      // failure can restore it (never blank). Set once — never overwrite with kmf-rendered HTML.
      if (cache.savedGrid === null) cache.savedGrid = g.innerHTML;
      g.innerHTML = '';
    }
    renderedCount = 0;
    feedHeld = [];
  }
  function showEmptyState() {
    const grid = document.querySelector(GRID);
    if (!grid) return;
    const msg = onSearch() ? 'No products match your search.' : 'No products match your filters.';
    grid.innerHTML = `<li class="grid__item kmf-grid-notice" style="grid-column:1/-1; text-align:center; padding:4rem 1rem;">
      <p style="font-size:1.5rem;">${msg} <a href="#" data-kmf-clear class="underlined-link">Clear all</a></p>
    </li>`;
    document.body.dataset.kmfReplaced = '1';
    updateCount(0, 0);
  }
  // Low-level append of already filtered+sorted items; never re-sorts existing cards.
  function appendItems(items, symbol, total, range) {
    const grid = document.querySelector(GRID);
    if (!grid || !items.length) return;
    grid.insertAdjacentHTML('beforeend', items.map(p => cardHtml(p, symbol, range)).join(''));
    renderedCount += items.length;
    document.body.dataset.kmfReplaced = '1';
    armCardLoaders(grid);
    updateCount(renderedCount, total);
  }

  // One honest grid-wide notice with a retry that reloads THIS page (filters and ?q= included).
  function showGridNotice(message) {
    const grid = document.querySelector(GRID);
    if (!grid) return;
    grid.innerHTML = `<li class="grid__item kmf-grid-notice" style="grid-column:1/-1; text-align:center; padding:4rem 1rem;">
      <p style="font-size:1.5rem;">${escapeHtml(message)} <a href="${escapeHtml(location.pathname + location.search)}" class="underlined-link">Try again</a></p>
    </li>`;
    updateCount(0, 0); // no product count describes a notice
  }

  // Typed search with no matches in a catalogue that failed to load: "no products match" would be a
  // guess. The server's grid is a true answer only for the query it was rendered for (the page-load
  // ?q=); for a query typed since, say the search could not run and offer a reload of THIS search.
  function showSearchFailure(q) {
    if (q === pageLoadQuery && cache.savedGrid !== null) { restoreServerGrid(); return; }
    showGridNotice('We couldn’t search the full catalogue right now.');
  }

  function restoreServerGrid() {
    const grid = document.querySelector(GRID);
    if (grid && cache.savedGrid !== null) {
      grid.innerHTML = cache.savedGrid;
      // The server's list is unfiltered (A–Z over the whole catalogue), but the page still says
      // "In stock only": keep its promise using the server's own sold-out flag (audit 2026-10-05 #2).
      if (readState().stock) {
        grid.querySelectorAll('li.grid__item').forEach((li) => {
          if (li.querySelector('.price--sold-out')) li.remove();
        });
        if (!grid.querySelector('li.grid__item')) showGridNotice('We couldn’t load the latest products right now.');
      }
    }
    restorePagination();
    document.body.dataset.kmfReplaced = '0';
    document.querySelector(CONTAINER)?.classList.remove('kmf-initializing');
  }

  // feedGen invalidates a scroll-advance that's mid-flight when a new apply() re-renders from the
  // top (e.g. filter change): the stale advance aborts (post-await gen check) instead of appending
  // onto the fresh grid. feedBusy serializes overlapping advances.
  let feedBusy = false, feedGen = 0, feedFirstPageError = false;
  // A transient first-page Storefront failure (cold compile / throttle) must not strand shoppers
  // on the un-filtered server grid: rebuild the spent pager and retry, backed off, before falling back.
  const MAX_FIRST_PAGE_RETRIES = 3, RETRY_BACKOFF_MS = 400, MAX_AVAIL_RETRIES = 2;
  const sleep = ms => new Promise(r => setTimeout(r, ms));

  async function renderFromTop(symbol) {
    const gen = ++feedGen;
    feedBusy = false;
    feedFirstPageError = false;
    renderReset();
    await advance(symbol, gen);
  }

  async function advance(symbol, gen) {
    if (gen === undefined) gen = feedGen;        // scroll callers run at the current generation
    if (feedBusy || gen !== feedGen) return;     // skip overlapping / stale advances
    feedBusy = true;
    try {
      const state = readState();
      if (onSearch() && state.q) {
        // TYPED SEARCH: full catalogue is loaded + stable; filter+sort once, window by renderedCount.
        const view = applySort(applyFilter(cache.products || [], state), state);
        if (gen !== feedGen) return;
        if (renderedCount === 0 && view.length === 0) {
          // Nothing found because the catalogue failed to load: never "no products match".
          if (cache.failed) { showSearchFailure(state.q); return; }
          showEmptyState();
          return;
        }
        appendItems(view.slice(renderedCount, renderedCount + WINDOW), symbol, view.length, priceRange(state));
      } else {
        // SERVER STREAM (collection + /search browse): pull the next Storefront page; the server has
        // already filtered + sorted, so just adapt + append. No client-side filter pass.
        if (feedExhausted || !feedPager) return;
        // Bounded: a page can come back EMPTY after orderByShownPrice held every card for the next
        // page (needs 50 consecutive multi-size products with a sold-out cheaper size — never
        // measured, but an empty grid stalls the scroll advance, so pull again, at most 3 times).
        let pg, hasMore = false, items = [];
        for (let pulls = 0; pulls < 3; pulls++) {
          pg = await feedPager.nextPage();
          if (gen !== feedGen) return;
          if (pg && pg.error && renderedCount === 0) { feedFirstPageError = true; feedExhausted = true; return; }
          hasMore = !!(pg && pg.hasMore);
          items = orderByShownPrice(((pg && pg.nodes) || []).map(toCardModel), state, hasMore);
          if (items.length || !hasMore) break;
        }
        // "No results" only when the feed is genuinely spent — never a false empty state.
        if (renderedCount === 0 && items.length === 0 && !hasMore) { showEmptyState(); feedExhausted = true; return; }
        appendItems(items, symbol, renderedCount + items.length, priceRange(state));
        if (!hasMore) feedExhausted = true;
      }
    } finally {
      feedBusy = false;
    }
  }

  // True when the user has scrolled within SCROLL_NEAR cards of the last rendered card.
  function nearEnd() {
    const grid = document.querySelector(GRID);
    if (!grid || grid.children.length === 0) return false;
    const idx = Math.max(0, grid.children.length - SCROLL_NEAR);
    const marker = grid.children[idx];
    if (!marker) return true;
    return marker.getBoundingClientRect().top <= window.innerHeight;
  }

  let scrollBound = false;
  function bindScroll(symbol) {
    if (scrollBound) return;
    scrollBound = true;
    const onScroll = debounce(() => {
      const s = readState();
      const more = (onSearch() && s.q)
        ? renderedCount < applyFilter(cache.products || [], s).length
        : !feedExhausted;
      if (more && nearEnd()) advance(symbol);
    }, 120);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // --- Main apply loop ---
  let applyToken = 0;
  async function apply() {
    const token = ++applyToken;
    const roots = document.querySelectorAll('[data-kmf-root]');
    if (roots.length === 0) return;
    const root = roots[0];
    const state = readState();
    const handle = root.dataset.kmfHandle;
    const symbol = root.dataset.kmfCurrencySymbol || '$';

    // kmf owns the grid the moment a root is present — kill the server infinite-scroll BEFORE any
    // (potentially multi-second) fetch so a scroll during that window can't append raw server pages.
    neutralizePagination();

    const typedSearch = onSearch() && !!state.q;

    if (typedSearch) {
      // CLIENT-SIDE full-load (products.json) — description (body_html) search needs the whole set.
      setLoading(true);
      if (!catalogueSettled()) { feedGen++; renderReset(); }
      // Single loader: Ripple + "Searching the full catalogue…" caption beneath it (no overlap with
      // a separate grid message — they used to collide).
      showCenteredLoader(true, 'Searching the full catalogue…');
      await Promise.all([loadFullCatalogue(), loadAvailability()]);
      if (token !== applyToken) return;
      document.querySelector(CONTAINER)?.classList.remove('kmf-initializing');
      roots.forEach(r => syncRoot(r, state, cache.products || [], true));
      await renderFromTop(symbol);
      bindScroll(symbol);
      showCenteredLoader(false);
      setLoading(false);
      return;
    }

    // SERVER-SIDE streamed path: collection page OR /search empty-browse. Rebuild the pager for the
    // current filter/sort selection so any change streams fresh from page 1.
    setLoading(true);
    showCenteredLoader(true);
    feedPager = buildPager(state, handle);
    feedExhausted = false;
    if (!feedPager) {
      // Storefront client absent → keep the server-rendered Liquid grid (graceful fallback).
      restoreServerGrid();
      showCenteredLoader(false);
      setLoading(false);
      return;
    }
    document.querySelector(CONTAINER)?.classList.remove('kmf-initializing');
    roots.forEach(r => syncRoot(r, state, [], false));
    await renderFromTop(symbol);   // renders first page; empty → empty state; error → retry below
    // Transient cold-Storefront error on the FIRST page: the pager marks itself done, so rebuild it
    // and retry (bounded, backed off) before the server-grid fallback. The loader stays up through
    // the retries, so the shopper sees a spinner — never the stale sold-out grid.
    let firstPageRetries = 0;
    while (feedFirstPageError && renderedCount === 0 && firstPageRetries < MAX_FIRST_PAGE_RETRIES) {
      firstPageRetries++;
      await sleep(RETRY_BACKOFF_MS * firstPageRetries);
      if (token !== applyToken) return;
      feedPager = buildPager(state, handle);
      feedExhausted = false;
      if (!feedPager) break;
      await renderFromTop(symbol);
    }
    if (token !== applyToken) return;
    if (feedFirstPageError && renderedCount === 0) restoreServerGrid();  // retries exhausted → graceful fallback
    bindScroll(symbol);
    showCenteredLoader(false);
    setLoading(false);
  }

  // --- Wire up ---
  function debounce(fn, ms) {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }
  const debouncedApply = debounce(apply, 200);

  function attach() {
    // CAPTURE-phase listeners: fire before native sidebarFiltersForm's form-level bubble listener,
    // and stopPropagation to prevent native AJAX from intercepting our inputs and wiping URL state.
    document.addEventListener('change', e => {
      const root = e.target.closest('[data-kmf-root]');
      if (!root) return;
      e.stopPropagation();
      const s = readState();
      if (e.target.dataset.kmfSort !== undefined) s.sort = e.target.value;
      else if (e.target.dataset.kmfStock !== undefined) s.stock = e.target.checked;
      else if (e.target.dataset.kmfTag) {
        const cat = e.target.dataset.kmfCat;
        const tag = e.target.dataset.kmfTag;
        if (e.target.checked) { if (!s[cat].includes(tag)) s[cat].push(tag); }
        else s[cat] = s[cat].filter(x => x !== tag);
      }
      writeState(s, root);
      apply();
    }, true);

    document.addEventListener('input', e => {
      const root = e.target.closest('[data-kmf-root]');
      if (!root) return;
      // Stop ANY input bubbling out of our root (prevents native form intercept)
      e.stopPropagation();
      if (e.target.dataset.kmfPriceMin === undefined && e.target.dataset.kmfPriceMax === undefined) return;
      const symbol = root.dataset.kmfCurrencySymbol || '$';
      const max = Number(root.dataset.kmfPriceCeiling || 0);
      const minEl = root.querySelector('input[data-kmf-price-min]');
      const maxEl = root.querySelector('input[data-kmf-price-max]');
      let lo = Number(minEl.value);
      let hi = Number(maxEl.value);
      if (lo > hi) {
        if (e.target.dataset.kmfPriceMin !== undefined) hi = lo; else lo = hi;
        minEl.value = lo;
        maxEl.value = hi;
      }
      root.querySelector('[data-kmf-price-min-display]').textContent = `${symbol}${lo}`;
      root.querySelector('[data-kmf-price-max-display]').textContent = `${symbol}${hi}`;
      const fillEl = root.querySelector('[data-kmf-price-fill]');
      if (fillEl && max > 0) {
        fillEl.style.left = (lo / max * 100) + '%';
        fillEl.style.right = ((max - hi) / max * 100) + '%';
      }
      const s = readState();
      s.pmin = lo > 0 ? lo : null;
      s.pmax = hi < max ? hi : null;
      writeState(s, root);
      debouncedApply();
    }, true);

    document.addEventListener('click', e => {
      const clear = e.target.closest('[data-kmf-clear]');
      if (clear) {
        e.preventDefault();
        e.stopPropagation();
        history.pushState({}, '', location.pathname);
        apply();
        return;
      }
      const pill = e.target.closest('[data-kmf-pill-kind]');
      if (pill) {
        e.preventDefault();
        e.stopPropagation();
        const root = pill.closest('[data-kmf-root]');
        const s = readState();
        const k = pill.dataset.kmfPillKind;
        if (k === 'stock') s.stock = false;
        else if (k === 'pmin') s.pmin = null;
        else if (k === 'pmax') s.pmax = null;
        else if (k === 'g' || k === 't') s[k] = s[k].filter(x => x !== pill.dataset.kmfPillTag);
        writeState(s, root);
        apply();
      }
    }, true);

    window.addEventListener('popstate', apply);

    // Free-text search box on /search — debounced live-filter of the already-loaded grid (no fetch
    // per keystroke; the full catalogue is cached). 3-char minimum; clearing shows everything again.
    const searchBox = document.querySelector('.template-search__search input[type="search"], #Search-In-Template');
    if (searchBox) {
      const runQuery = () => {
        const term = searchBox.value.trim();
        const sp = new URLSearchParams(location.search);
        if (term.length >= 3) sp.set('q', term); else sp.delete('q');
        const qs = sp.toString();
        history.replaceState({}, '', location.pathname + (qs ? '?' + qs : ''));
        apply();
      };
      searchBox.addEventListener('input', debounce(runQuery, 500));
      // The box lives in a native <form method="get"> — pressing Enter would full-reload to
      // /search?q=… and DROP ?qf=. Typing already searches live, so intercept submit.
      const searchForm = searchBox.closest('form');
      if (searchForm) {
        searchForm.addEventListener('submit', e => { e.preventDefault(); runQuery(); });
      }
    }

    // Reflect ?qf= from the URL onto ALL chips. Called on load AND after every toggle.
    function syncQfChips() {
      const active = readState().qf;
      document.querySelectorAll('[data-kmf-qf]').forEach(c => {
        const on = active.includes(c.dataset.kmfQf);
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', String(on));
      });
    }
    document.querySelectorAll('[data-kmf-qf]').forEach(chip => {
      chip.addEventListener('click', () => {
        const root = document.querySelector('[data-kmf-root]');
        if (!root) return;
        const field = chip.dataset.kmfQf;
        const s = readState();
        const i = s.qf.indexOf(field);
        if (i === -1) s.qf.push(field); else s.qf.splice(i, 1);
        if (!s.qf.length) s.qf = ['name']; // never zero match fields
        writeState(s, root);
        syncQfChips();
        apply();
      });
    });
    syncQfChips(); // initial reflect of ?qf= from the URL (deep-link / back-forward)
  }

  function init() {
    attach();
    document.querySelector(CONTAINER)?.classList.add('kmf-initializing');
    apply();
  }
  // KM2Deps.ready: 'interactive' means parsed but DEFERRED SCRIPTS NOT YET RUN, so the old
  // `else init()` fired before the shared globals existed. Only 'complete' runs immediately.
  (window.KM2Deps ? window.KM2Deps.ready(init)
    : (document.readyState === 'complete' ? init()
       : document.addEventListener('DOMContentLoaded', init, { once: true })));
})();
